import { useState, useEffect, useRef } from 'react';
import { isEqual, set as lodashSet,get as lodashGet, unset as lodashUnset, cloneDeep } from 'lodash';

/**
 * Custom hook to manage form state for channel settings, with support for nested objects.
 * @param {Object} initialSettings - Initial settings for the channel
 * @returns {Object} Form state and handlers
 */
const useChannelForm = (initialSettings = {}) => {
  const [values, setValues] = useState(cloneDeep(initialSettings));
  const [hasChanges, setHasChanges] = useState(false);
  const originalSettings = useRef(cloneDeep(initialSettings));

  useEffect(() => {
    // Deep copy to avoid reference issues
   const newInitialSettings = cloneDeep(initialSettings);

    setValues(newInitialSettings);
    originalSettings.current = newInitialSettings;
    setHasChanges(false); // Reset changes on prop update
  }, [initialSettings]);

  const checkForChanges = (currentValues) => {
    const hasActualChanges = !isEqual(currentValues, originalSettings.current);
    setHasChanges(hasActualChanges);
  };

  // Handle field change for both top-level and nested properties
  const handleChange = (path, value) => {
    setValues(prev => {
      // Use a deep copy to ensure state updates correctly
      const newValues = cloneDeep(prev);

      // Use lodash's 'set' to safely update nested properties via a path string
      lodashSet(newValues, path, value);
      checkForChanges(newValues);
      return newValues;
    });
  };
 const getValuesForSave = ({ pruneUnchangedPaths = [] } = {}) => {
   const payload = cloneDeep(values);
   for (const path of pruneUnchangedPaths) {
     const curr = lodashGet(values, path);
     const orig = lodashGet(originalSettings.current, path);
     if (isEqual(curr, orig)) {
       lodashUnset(payload, path);
     }
   }
   return payload;
 };

  return {
    values,
    handleChange,
    hasChanges,
    getValuesForSave
  };
};

export default useChannelForm;